import { useEffect, useMemo, useRef, useState } from 'react';
import {
   HiArrowNarrowLeft,
   HiCheck,
   HiCheckCircle,
   HiExclamationCircle,
   HiLocationMarker,
   HiOutlineCheck,
   HiOutlineCheckCircle,
   HiOutlineChevronDown,
   HiOutlineClock,
   HiOutlineDuplicate,
   HiOutlineExclamationCircle,
   HiOutlineRefresh,
   HiOutlineShieldExclamation,
   HiOutlineX,
   HiPlus,
   HiWifi,
   HiXCircle,
} from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { SelectOption } from 'src/components/admin/form/selectField';
import { BookingFailureLogResponse, BookingFailureLogStatus, BookingFailureLogStatusType } from 'src/types/bookingFailureLog.type';
import { DATE_FORMAT, INITIAL_ZOOM, routePaths } from 'src/utils/constants';
import { toSentenceCase } from 'src/utils/string.util';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { twMerge } from 'tailwind-merge';
import SimpleMapView from 'src/components/MapView/SimpleMapView';
import { ReactComponent as MobileDeviceIcon } from 'assets/image/svg/device.svg';
import { ReactComponent as DesktopDeviceIcon } from 'assets/image/svg/ic-desktop-device.svg';
import { showToast } from 'src/components/toast';
import { useGetBookingFailureLog, useUpdateBookingFailureStatus, useCreateBookingFailureLogNote } from 'src/hooks/useBookingFailureLogs';
import Loading from 'src/components/loading';
import moment from 'moment';
import { formatUtcDateTime, formatDateOnly } from 'src/utils/date.util';
import { numberFormat } from 'src/utils/utils';
import { capitalize } from 'src/utils/room.util';
import { useAuth } from 'src/contexts/AuthContext';

const handleCopyToClipboard = async ({ title, value }: { title: string; value: string }) => {
   await navigator.clipboard.writeText(value);

   showToast('success', `${title} is copied to clipboard`);
};

const SectionTitle: React.FC<{ title: string; className?: string }> = ({ className, title }) => {
   return <h3 className={twMerge('font-bold text-black mb-2 text-base', className)}>{title}</h3>;
};

const SectionKeyValue: React.FC<{ title: string; value?: string; className?: string; hasCopyIcon?: boolean }> = ({
   title,
   value,
   className,
   hasCopyIcon,
}) => {
   return (
      <div className={twMerge('grid grid-cols-2 md:grid-cols-[1fr_2fr] gap-x-2.5', className)}>
         <div className="text-twGray text-sm title">{title}</div>
         <div className="flex items-center gap-1.25 justify-end">
            <p className="text-black text-sm text-right value truncate whitespace-pre-wrap font-semibold">{value && value !== '' ? value : '-'}</p>
            {hasCopyIcon && value && (
               <div className="p-0.5 cursor-pointer">
                  <HiOutlineDuplicate
                     className="w-4.5 h-4.5 text-primary"
                     onClick={async e => {
                        e.stopPropagation();
                        await handleCopyToClipboard({ title, value: value && value !== '' ? value : '-' });
                     }}
                  />
               </div>
            )}
         </div>
      </div>
   );
};

const BookingFailureDetails = () => {
   const [t] = useTranslation();
   const navigate = useNavigate();
   const { id } = useParams() || {};

   // Fetch get booking failure logs
   const { data: getBookingFailureLogs, isLoading } = useGetBookingFailureLog(id as string);

   // Update status
   const { mutateAsync: updateBookingFailureStatus, isPending } = useUpdateBookingFailureStatus({
      onSuccessCallBack: () => {
         showToast('success', 'Successfully updated status');
      },
      onErrorCallBack(_error) {
         showToast('error', 'Error while updating status');
      },
   });

   // Create note
   const { mutateAsync: createBookingFailureLogNote } = useCreateBookingFailureLogNote({
      onSuccessCallBack: () => {
         setIsEditingNote(false);
         setNoteValue('');
         showToast('success', 'Note added successfully');
      },
      onErrorCallBack: () => {
         showToast('error', 'Error while adding note');
      },
   });

   const statusOptions: SelectOption[] = BookingFailureLogStatus.map(key => {
      return { label: toSentenceCase(key), value: key };
   });

   const options = useMemo(() => {
      const convertItem = (value: BookingFailureLogStatusType) => {
         switch (value) {
            case 'NEW':
               return { value: 'NEW', label: 'New', color: 'bg-red-100 text-red-600 hover:bg-red-200', icon: HiOutlineExclamationCircle };

            case 'INVESTIGATING':
               return {
                  value: 'INVESTIGATING',
                  label: 'Investigating',
                  color: 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200',
                  icon: HiOutlineRefresh,
               };

            case 'RESOLVED':
               return { value: 'RESOLVED', label: 'Resolved', color: 'bg-green-200 text-green-600 hover:bg-green-300', icon: HiOutlineCheckCircle };

            case 'IGNORED':
               return { value: 'IGNORED', label: 'Ignored/Invalid', color: 'bg-gray-200 text-gray-600 hover:bg-gray-300', icon: HiOutlineX };
         }
      };

      return statusOptions?.map((status: { value: string }) => {
         return convertItem(status.value as BookingFailureLogStatusType);
      });
   }, [statusOptions]);

   const selectedBookingFailure: BookingFailureLogResponse = useMemo(() => {
      return getBookingFailureLogs?.data;
   }, [getBookingFailureLogs?.data]);

   const [open, setOpen] = useState(false);
   const [selectedStatus, setSelectedStatus] = useState(options?.[1]);

   type NetworkQuality = 'fast' | 'weak' | 'low';

   const getNetworkQuality = ({ effectiveType, downlink }: { effectiveType?: string; downlink?: number }): NetworkQuality => {
      if (typeof downlink === 'number' && !Number.isNaN(downlink)) {
         if (downlink >= 10) return 'fast';
         if (downlink >= 5) return 'fast';
         if (downlink >= 1.5) return 'weak';
         return 'low';
      }

      switch (effectiveType) {
         case '4g':
            return 'weak';
         case '3g':
            return 'weak';
         case '2g':
         case 'slow-2g':
            return 'low';
         default:
            return 'fast';
      }
   };

   const { infomation } = selectedBookingFailure?.metadata || {};
   const longitude = infomation?.location ? Number(JSON.parse(infomation.location)?.center?.[0]) : -117.94433;
   const latitude = infomation?.location ? Number(JSON.parse(infomation.location)?.center?.[1]) : 37.7749;
   const placeName = infomation?.location ? JSON.parse(infomation.location)?.placeName : 'San Francisco, CA, USA';
   const effectiveType = infomation?.network ? JSON.parse(infomation.network)?.effectiveType : '4g';
   const downlink = infomation?.network ? Number(JSON.parse(infomation.network)?.downlink) : 10;
   const relatedLogs = useMemo(
      () =>
         selectedBookingFailure?.relatedLogs?.map(log => ({
            device: log.metadata?.infomation?.device,
            failure: `${log?.step}: ${log?.errorDetail?.type}`,
         })),
      [selectedBookingFailure?.relatedLogs],
   );

   const statusHistories = useMemo(
      () =>
         selectedBookingFailure?.statusHistories?.map(history => ({
            id: history.id,
            oldStatus: history.oldStatus,
            newStatus: history.newStatus,
            createdBy: history.createdByUser,
            created: history.created,
            notes: history.notes,
         })),
      [selectedBookingFailure?.statusHistories],
   );
   const [isEditingNote, setIsEditingNote] = useState(false);
   const [noteValue, setNoteValue] = useState('');
   const { user, isAdmin } = useAuth();

   useEffect(() => {
      const bookingFailureLog = getBookingFailureLogs?.data;
      const selectedOption = options?.find(o => o.value === bookingFailureLog?.status);

      if (selectedOption) setSelectedStatus(selectedOption);
   }, [selectedBookingFailure]);

   const ref = useRef<any>(null);

   useEffect(() => {
      const handleMouseDown = (event: any) => {
         if (ref.current && !ref.current.contains(event.target)) {
            setOpen(false);
         }
      };

      document.addEventListener('mousedown', handleMouseDown);

      return () => document.removeEventListener('mousedown', handleMouseDown);
   }, []);

   const handleCreateNote = ({ statusHistoryId, bookingErrorLogId }: { statusHistoryId?: string; bookingErrorLogId: string }) => {
      if (!noteValue.trim()) return;

      createBookingFailureLogNote({
         note: noteValue,
         bookingErrorLogId,
         statusHistoryId,
      });
   };

   return isLoading || isPending ? (
      <Loading />
   ) : (
      <div className="p-6">
         {/* Header */}
         <header className="mb-7.5 flex items-start">
            <button
               className="w-6 h-7.5 flex items-center cursor-pointer"
               onClick={() => {
                  navigate(routePaths.ADMIN.BOOKING_FAILURE_TRACKER.INDEX);
               }}
            >
               <HiArrowNarrowLeft color="#686868" size={18} />
            </button>

            <div className="flex flex-col gap-2.5">
               <div className="flex items-center gap-3.75">
                  <h1 className="text-xl font-bold leading-6">Booking Failure Details</h1>
                  <div className="relative w-[198px]" ref={ref}>
                     <button
                        onClick={() => setOpen(!open)}
                        className={`flex items-center justify-between px-2.5 py-1.25 rounded-md text-sm ${selectedStatus ? selectedStatus.color : 'bg-white border'}`}
                     >
                        <selectedStatus.icon className="h-3.5 w-3.5 mr-1.25" />
                        <p className="font-semibold">{selectedStatus?.label}</p>
                        <HiOutlineChevronDown className="h-4.5 w-4.5 ml-3.75 text-black" />
                     </button>

                     {/* Dropdown menu */}
                     {open && (
                        <ul className="absolute flex flex-col mt-0.5 w-full rounded-md shadow-lg bg-white z-10 gap-[1px]">
                           {options?.map((opt: any) => (
                              <li
                                 key={opt.value}
                                 onClick={() => {
                                    updateBookingFailureStatus({ id: id as string, payload: { status: opt.value } });
                                    setSelectedStatus(opt);

                                    setOpen(false);
                                 }}
                                 className={`cursor-pointer py-2 px-2.5 flex items-center text-sm ${opt.color}`}
                              >
                                 <opt.icon className="h-3.5 w-3.5" />
                                 <span className="min-w-32 mx-2.5 font-semibold">{opt.label}</span>
                                 {selectedStatus?.label === opt.label && <HiOutlineCheck className="w-4 h-4 text-twGreen" />}
                              </li>
                           ))}
                        </ul>
                     )}
                  </div>
               </div>
               <span className="text-base text-twGray">
                  <strong className="font-semibold">{selectedBookingFailure?.id} • </strong>
                  {formatUtcDateTime(selectedBookingFailure?.created, 'MMM D, YYYY, h:mm A')}
               </span>
            </div>
         </header>

         {/* Main Content */}
         <div className="flex flex-col md:flex-row gap-6">
            {/* Left Column (flex-1) */}
            <div className="flex-1 space-y-4">
               {/* Alert */}
               {selectedBookingFailure?.errorDetail ? (
                  <div className="bg-orange-50 border border-orange-200 rounded-10 p-3.75 flex items-start justify-start text-orange-600 gap-4">
                     <div className="py-1">
                        <HiOutlineClock className="h-6 w-6" />
                     </div>
                     <div>
                        <h2 className="font-bold text-1.5xl mb-2.5">{selectedBookingFailure?.errorDetail?.type}</h2>
                        <p className="text-base">{selectedBookingFailure?.errorDetail?.message}</p>
                     </div>
                  </div>
               ) : null}

               {/* Stack Trace */}
               <div className="bg-white border border-border rounded-10 p-3.75 relative">
                  <h3 className="font-semibold mb-2">Stack Trace</h3>
                  <div className="relative">
                     {selectedBookingFailure?.errorDetail ? (
                        <div className="absolute p-2.5 rounded-6 bg-twGray text-white top-2.5 right-2.5 cursor-pointer hover:bg-gray-500">
                           <HiOutlineDuplicate
                              className="w-6 h-6"
                              onClick={e => {
                                 e.stopPropagation();

                                 handleCopyToClipboard({
                                    title: 'Stack Trace',
                                    value: JSON.stringify(selectedBookingFailure?.errorDetail ?? '', null, 2),
                                 });
                              }}
                           />
                        </div>
                     ) : null}
                     <SyntaxHighlighter
                        language="json"
                        style={atomDark}
                        customStyle={{
                           fontSize: '14px',
                           lineHeight: '1.4',
                           borderRadius: '10px',
                           padding: '10px',
                           whiteSpace: 'pre-wrap',
                        }}
                     >
                        {JSON.stringify(selectedBookingFailure?.errorDetail ?? '', null, 2)}
                     </SyntaxHighlighter>
                  </div>
               </div>

               {/* Booking + User Details */}
               <div className="flex flex-col xl:flex-row gap-4">
                  <div className="flex-1 bg-white border border-border rounded-10 p-3.75">
                     <SectionTitle title="Booking Details" />
                     <ul className="text-sm text-gray-600 space-y-1">
                        <SectionKeyValue title="Booking ID" value={selectedBookingFailure?.booking?.id} hasCopyIcon />
                        <SectionKeyValue
                           title="Dates"
                           value={
                              selectedBookingFailure?.booking?.checkinDate &&
                              selectedBookingFailure?.booking?.checkoutDate &&
                              `${formatDateOnly(selectedBookingFailure?.booking?.checkinDate.split('T')[0], DATE_FORMAT.SERVER_DATE)} → ${formatDateOnly(selectedBookingFailure?.booking?.checkoutDate.split('T')[0], DATE_FORMAT.SERVER_DATE)}`
                           }
                        />
                        <SectionKeyValue
                           title="Amount"
                           value={
                              selectedBookingFailure?.booking?.paidAmount
                                 ? numberFormat(Number(selectedBookingFailure?.booking?.paidAmount))
                                 : undefined
                           }
                        />
                        <SectionKeyValue title="Guests" value={selectedBookingFailure?.booking?.adultNumber?.toString()} />
                        <SectionKeyValue title="Property" value={selectedBookingFailure?.booking?.metadata?.hotelName} />
                     </ul>
                  </div>

                  <div className="flex-1 bg-white border border-border rounded-10 p-3.75">
                     <div className="flex items-center justify-between mb-2">
                        <SectionTitle title="User Details" className="mb-0" />
                        {isAdmin && selectedBookingFailure?.user?.id ? (
                           <button
                              className="flex items-center gap-1 text-xs font-semibold text-primary cursor-pointer"
                              onClick={() => navigate(routePaths.ADMIN.FRAUD_WATCH.DETAIL.replace(':userId', selectedBookingFailure.user.id))}
                           >
                              <HiOutlineShieldExclamation className="w-3.5 h-3.5" />
                              {t('admin.fraudWatch.viewInFraudWatch')}
                           </button>
                        ) : null}
                     </div>
                     <ul className="text-sm text-gray-600 space-y-1">
                        <SectionKeyValue
                           title="Name"
                           value={`${selectedBookingFailure?.user?.firstName ?? ''} ${selectedBookingFailure?.user?.lastName ?? ''}`}
                        />
                        <SectionKeyValue title="Email" value={selectedBookingFailure?.user?.emailAddress} hasCopyIcon />
                        <SectionKeyValue title="User ID" value={selectedBookingFailure?.user?.id} hasCopyIcon />
                        <SectionKeyValue title="IP Address" value={selectedBookingFailure?.user?.ip} hasCopyIcon />
                     </ul>
                  </div>
               </div>

               {/* Metadata */}
               <div className="flex-1 bg-lightGray border border-border rounded-10 p-3.75">
                  <SectionTitle title="Metadata" />
                  <ul className="text-sm text-gray-600 space-y-1">
                     <SectionKeyValue title="ip" value={selectedBookingFailure?.user?.ip} />
                     <SectionKeyValue title="os" value={infomation?.os} />
                     <SectionKeyValue title="device" value={infomation?.device} />
                     <SectionKeyValue title="browser" value={infomation?.browser} />
                     <SectionKeyValue title="userAgent" value={[infomation?.browser, infomation?.os].filter(Boolean).join('/') || undefined} />
                     <SectionKeyValue
                        title="network"
                        value={effectiveType ? `${getNetworkQuality({ effectiveType, downlink })} (${effectiveType.toUpperCase()})` : undefined}
                     />
                     <SectionKeyValue title="location" value={placeName} />
                  </ul>
               </div>
            </div>

            {/* Right Column (max-w-300px) */}
            <div className="flex flex-col w-full md:max-w-[400px] space-y-4">
               {/* Active Logs */}
               <div className="bg-white border border-border rounded-10 p-3.75">
                  <SectionTitle title="Active Logs" />
                  {statusHistories && statusHistories.length
                     ? statusHistories.map((history, idx) => (
                          <div key={history.id}>
                             {/* Log entry */}
                             <div className="mb-3.75 flex flex-col gap-4">
                                {history.newStatus === 'NEW' ? (
                                   <div className="flex items-start justify-start gap-1.25">
                                      <div className="p-0.5">
                                         {statusHistories.length - 1 === idx ? (
                                            <HiCheckCircle className="w-4 h-4 text-twGreen" />
                                         ) : (
                                            <HiExclamationCircle className="w-4 h-4 text-twGray" />
                                         )}
                                      </div>
                                      <div className="w-full">
                                         <div className="flex justify-between items-center">
                                            <span className="text-sm">
                                               Status changed to <strong>NEW</strong>{' '}
                                               {history.createdBy ? `by ${history.createdBy.firstName} ${history.createdBy.lastName}` : ''}
                                            </span>
                                            <span className="text-sm text-twGray">{formatUtcDateTime(history.created, 'hh:mm A')}</span>
                                         </div>
                                         <div className="text-2xs text-gray-500 mt-1">
                                            <div>
                                               {idx === 0
                                                  ? 'Automated'
                                                  : getNetworkQuality({
                                                       effectiveType,
                                                       downlink,
                                                    })}{' '}
                                               {effectiveType.toUpperCase()}{' '}
                                            </div>
                                            <ul className="text-sm text-gray-600 space-y-1 list-disc pl-5">
                                               {history.notes
                                                  ? history.notes?.map((note, index) => (
                                                       <li key={index} className="whitespace-pre-wrap">
                                                          <p className="whitespace-pre-wrap text-xs text-black italic">{note.note}</p>
                                                       </li>
                                                    ))
                                                  : null}
                                            </ul>
                                         </div>
                                      </div>
                                   </div>
                                ) : (
                                   <div className="flex items-start justify-start gap-1.25">
                                      <div className="p-0.5">
                                         {statusHistories.length - 1 === idx ? (
                                            <HiCheckCircle className="w-4 h-4 text-twGreen" />
                                         ) : (
                                            <HiExclamationCircle className="w-4 h-4 text-twGray" />
                                         )}
                                      </div>
                                      <div className="w-full">
                                         <div className="flex justify-between items-center">
                                            <span className="text-sm">
                                               <strong>{capitalize(history.newStatus.toLocaleLowerCase())}</strong> by {history.createdBy?.firstName}{' '}
                                               {history.createdBy?.lastName}
                                            </span>
                                            <span className="text-sm text-twGray">{formatUtcDateTime(history.created, 'hh:mm A')}</span>
                                         </div>
                                         <div className="text-2xs text-gray-500 mt-1">
                                            {' '}
                                            <div>
                                               {getNetworkQuality({
                                                  effectiveType,
                                                  downlink,
                                               })}{' '}
                                               {effectiveType.toUpperCase()}
                                            </div>
                                            <ul className="text-sm text-gray-600 space-y-1 list-disc pl-5">
                                               {history.notes
                                                  ? history.notes?.map((note, index) => (
                                                       <li key={index} className="whitespace-pre-wrap">
                                                          <p className="whitespace-pre-wrap text-xs text-black italic">{note.note}</p>
                                                       </li>
                                                    ))
                                                  : null}
                                            </ul>
                                         </div>
                                      </div>
                                   </div>
                                )}
                             </div>

                             {/* Note section */}
                             {idx === statusHistories.length - 1 ? (
                                <>
                                   {/* Header */}
                                   <div className="border-t border-l border-r border-checkbox rounded-tr-6 rounded-tl-6 p-2.5 h-11 flex justify-between items-center bg-lightGray">
                                      <p className="text-sm font-semibold">Note</p>

                                      <div
                                         className="p-1.5 cursor-pointer rounded-full transition-all duration-200 hover:bg-gray-100"
                                         onClick={() => {
                                            setIsEditingNote(true);
                                            setNoteValue('');
                                         }}
                                      >
                                         <HiPlus className="h-5 w-5 text-primary" />
                                      </div>
                                   </div>

                                   {/* Body */}
                                   <div className="border border-checkbox rounded-br-6 rounded-bl-6 p-2.5 min-h-[90px] text-sm text-twGray">
                                      {isEditingNote ? (
                                         <div className="relative">
                                            <div className="whitespace-pre-wrap text-sm text-black">
                                               Admin <span className="text-sm text-twGray">[{`${user?.firstName} ${user?.lastName}`}]</span>
                                               <span className="text-sm text-twGray"> {moment().format('hh:mm A')}</span>
                                            </div>
                                            <textarea
                                               className="w-full min-h-[90px] pr-10 outline-none resize-none"
                                               value={noteValue}
                                               onChange={e => setNoteValue(e.target.value)}
                                               onKeyDown={e => {
                                                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                                                     e.preventDefault();
                                                     handleCreateNote(
                                                        idx === 0
                                                           ? { bookingErrorLogId: selectedBookingFailure?.id }
                                                           : { bookingErrorLogId: selectedBookingFailure?.id, statusHistoryId: history.id },
                                                     );
                                                  }
                                               }}
                                               autoFocus
                                               maxLength={150}
                                            />

                                            {/* Save icon (Messenger style) */}
                                            <div
                                               className="absolute bottom-0 right-0 p-1.5 cursor-pointer bg-white rounded-full shadow hover:bg-gray-100"
                                               onClick={() =>
                                                  handleCreateNote(
                                                     idx === 0
                                                        ? { bookingErrorLogId: selectedBookingFailure?.id }
                                                        : { bookingErrorLogId: selectedBookingFailure?.id, statusHistoryId: history.id },
                                                  )
                                               }
                                            >
                                               <HiCheck className="h-5 w-5 text-primary" />
                                            </div>
                                         </div>
                                      ) : null}
                                   </div>
                                </>
                             ) : null}
                          </div>
                       ))
                     : null}
               </div>

               {/* Checkout Timeline */}
               <div className="bg-white border border-border rounded-10 p-3.75">
                  <SectionTitle title="Checkout Timeline" />
                  <ul className="text-sm text-gray-600 space-y-1">
                     <div className="flex items-start justify-start gap-1.25">
                        <div className="py-0.5">
                           <HiCheckCircle className="w-4 h-4 text-twGreen" />
                        </div>
                        <span className="text-sm">Room Selection</span>
                     </div>
                     <div className="flex items-start justify-start gap-1.25">
                        <div className="py-0.5">
                           <HiXCircle className="w-4 h-4 text-twRed" />
                        </div>
                        <div className="w-full">
                           <div className="flex justify-between items-center">
                              <span className="text-sm text-twRed">Payment Processing</span>
                              <span className="text-sm text-twGray">{formatUtcDateTime(selectedBookingFailure?.created, 'hh:mm A')}</span>
                           </div>
                           <div className="text-2xs text-twRed mt-1">{`${selectedBookingFailure?.step}: ${selectedBookingFailure?.errorDetail?.type}`}</div>
                        </div>
                     </div>
                  </ul>
               </div>

               {/* Device & Context */}
               <div className="flex-1 bg-white border border-border rounded-10 p-3.75">
                  <SectionTitle title="Device & Context" className="mb-0" />

                  {/* Device */}
                  <div className="flex items-center gap-4 p-3.75 w-fit">
                     {/* Smartphone outline */}
                     {infomation?.device !== 'desktop' ? <MobileDeviceIcon /> : <DesktopDeviceIcon />}

                     {/* Text info */}
                     <div className="flex flex-col text-base gap-1.25">
                        <span className="font-bold">{infomation?.device ? capitalize(infomation?.device) : 'Desktop'}</span>
                        <span className="font-semibold">{infomation?.os}</span>
                        <span className="font-semibold">{infomation?.browser}</span>
                     </div>
                  </div>

                  {/* Map box */}
                  <div>
                     <div className="max-h-[140px] rounded-10 overflow-hidden">
                        <SimpleMapView
                           zoom={INITIAL_ZOOM}
                           id={'best_western_california_city_inn_suites'}
                           latitude={latitude}
                           longitude={longitude}
                           interactive={true}
                           hideAttControl
                           iconSize={30}
                           markerColor="text-primary"
                        />
                     </div>

                     <div className="flex justify-between items-center mt-3.75">
                        <div className="flex justify-between items-center gap-[3px]">
                           <HiLocationMarker className="w-5 h-5 text-twGray" />
                           <span className="text-sm">{placeName}</span>
                        </div>
                        <div className="flex justify-between items-center gap-[3px]">
                           <HiWifi className="w-5 h-5 text-twGray" />
                           <span className="text-sm">
                              {getNetworkQuality({
                                 effectiveType,
                                 downlink,
                              })}{' '}
                              {effectiveType.toUpperCase()}
                           </span>
                        </div>
                     </div>
                  </div>

                  {/* Previous failures & Status lines*/}
                  <div className="w-full space-y-3.75 mt-3.75">
                     {/* Previous failures */}
                     {relatedLogs && relatedLogs.length ? (
                        <div>
                           <h3 className="text-sm font-bold">Previous device-specific failures</h3>
                           <ul className="text-xs">
                              {relatedLogs?.map((log, index) => (
                                 <li key={index}>
                                    {log.device} - {log.failure}
                                 </li>
                              ))}
                           </ul>
                        </div>
                     ) : null}

                     {/* Status lines */}
                     <div className="space-y-1">
                        <p className="text-xs">
                           Won Booking: <span className="text-xs text-twGreen">No</span>
                        </p>
                        <p className="text-xs">
                           Checkout Attempted:{' '}
                           <span className="text-xs text-twGreen">{selectedBookingFailure?.step === 'FINAL_BOOKING' ? 'Yes' : 'No'}</span>
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
};

export default BookingFailureDetails;
